import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/**
 * En kodebit som tåler at en utrulling har flyttet filene under beina på oss.
 *
 * ── Feilen dette finnes for ────────────────────────────────────────────────
 *
 * Meldt fra bruk: «de planla en rute, og da de rullet ned til kartet krasjet
 * det liksom og lastet hjemskjermen på nytt».
 *
 * Kjeden, verifisert ledd for ledd:
 *
 *   1. Kartet lastes for seg: `lazy(() => import("./components/TerrainMap3D"))`.
 *      Filnavnet har en hash — `TerrainMap3D-BPp4XSxJ.js` — og den endres ved
 *      hver bygging.
 *   2. Vi ruller ut en ny versjon. Cloudflare serverer bare filene i det nye
 *      manifestet, så den gamle hashen svarer **404**. Målt:
 *
 *          /assets/TerrainMap3D-BPp4XSxJ.js     200
 *          /assets/TerrainMap3D-GAMMELHASH.js   404
 *
 *   3. Fanen som allerede sto åpen kjører fortsatt den gamle koden. Den ber om
 *      den gamle chunken i det brukeren ruller ned — for det er *da* kartet
 *      første gang trengs.
 *   4. `import()` avvises. `React.lazy` kaster avvisningen videre **under
 *      render**.
 *   5. `<Suspense>` fanger *ventende*, ikke *avvist*. Det krever en
 *      feilgrense, og appen hadde ingen.
 *   6. React avmonterer hele treet. Siden blir blank.
 *   7. Brukeren laster på nytt — og lander på skjemaet.
 *
 * Hvert ord i meldingen stemmer, inkludert «da de rullet ned»: det er det
 * eneste tidspunktet den kodebiten hentes.
 *
 * ── Hvorfor svaret er å laste siden på nytt ────────────────────────────────
 *
 * Fordi filen ikke *finnes*. Det er ingenting å prøve på nytt — den gamle
 * versjonen er borte, og den eneste måten å få tak i den nye på er å hente
 * `index.html` igjen. En feilmelding ville vært å be brukeren gjøre for hånd
 * det vi vet er riktig.
 *
 * Planen overlever: den ligger i `localStorage`, og appen henter den fram ved
 * oppstart. Se lib/offline.ts.
 *
 * ── Hvorfor det bare skjer én gang ─────────────────────────────────────────
 *
 * Feiler den *igjen* etter en omlasting, er det ikke en utrulling — da er
 * nettet borte, eller filen er blokkert. Å laste på nytt en gang til ville
 * gitt en løkke, altså en side som blinker i det uendelige. Andre gang kastes
 * feilen videre til feilgrensa, som sier fra med ord.
 *
 * Merket ligger i `sessionStorage` og ikke i `localStorage`: det gjelder denne
 * fanen og denne økten. En bruker som kommer tilbake i morgen skal få sin ene
 * omlasting på nytt.
 */

const MARK = "reise-planer-chunk-reload:";

function alreadyReloadedFor(name: string): boolean {
  try {
    return sessionStorage.getItem(MARK + name) === "1";
  } catch {
    /*
     * Privat vindu eller blokkert lagring. Da vet vi ikke om vi har lastet på
     * nytt før, og svaret må være «nei»: det verste utfallet er én omlasting
     * for mye, mot en app som blir stående blank.
     */
    return false;
  }
}

function rememberReloadFor(name: string): void {
  try {
    sessionStorage.setItem(MARK + name, "1");
  } catch {
    // Ikke kritisk. Uten merket blir vernet mot løkke borte, men se over:
    // uten lagring er det uansett ingenting å huske i.
  }
}

function forgetReloadFor(name: string): void {
  try {
    sessionStorage.removeItem(MARK + name);
  } catch {
    // Se over.
  }
}

/**
 * `lazy()`, men med én omlasting i bakhånd når kodebiten er borte.
 *
 * `name` er nøkkelen merket lagres under, så to kodebiter ikke bruker opp
 * hverandres ene forsøk.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyChunk<T extends ComponentType<any>>(
  name: string,
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    load()
      .then((module) => {
        /*
         * Den lastet. Merket ryddes, så en *senere* utrulling i den samme
         * fanen får sin egen ene omlasting.
         */
        forgetReloadFor(name);
        return module;
      })
      .catch((cause: unknown) => {
        if (alreadyReloadedFor(name)) throw cause;

        rememberReloadFor(name);
        console.warn(`[chunk] «${name}» er borte — henter appen på nytt.`, cause);
        window.location.reload();

        /*
         * Et løfte som aldri innfris. Siden er på vei bort, og React skal bli
         * stående i `<Suspense>` til den er det — å kaste her ville blinket en
         * feilmelding i det halve sekundet omlastingen tar.
         */
        return new Promise<never>(() => {});
      }),
  );
}
